
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Brain, Users, ShoppingCart, Check } from 'lucide-react';
import HomepageGuestHeader from './HomepageGuestHeader';
import EarlyAccessBanner from './EarlyAccessBanner';
import SEOHead from '@/components/SEOHead';
import ProductScreenshots from './ProductScreenshots';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { AppleSignInButton } from '@/components/auth/AppleSignInButton';
import { toast } from 'sonner';
import { isMobileDevice } from '@/lib/utils';
import { HeroSectionWeb } from '@/components/landing/HeroSectionWeb';

const GuestLanding = () => {
  const navigate = useNavigate();
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const handleAuthSuccess = () => {
    navigate('/');
  };

  const handleAuthError = (error: string) => {
    console.error('Auth error:', error);
    toast.error(error);
  };
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "name": "forkast - AI-Powered Family Meal Planning",
        "applicationCategory": "LifestyleApplication",
        "operatingSystem": "Web",
        "description": "AI-powered family meal planning app that helps busy families organize their weekly dinners, get smart dinner suggestions, share the mental load, and build better eating habits with intelligent recommendations.",
        "url": "https://forkast.in",
        "author": {
          "@type": "Organization",
          "name": "forkast Team"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "featureList": [
          "AI-powered dinner suggestions",
          "Recipe collection management",
          "Family meal planning calendar",
          "Smart shopping list generation",
          "Wellness insights and analytics"
        ]
      },
      {
        "@type": "Organization",
        "name": "forkast",
        "description": "AI-powered family meal planning platform helping busy families organize weekly dinners and build better eating habits.",
        "url": "https://forkast.in",
        "logo": "https://forkast.in/lovable-uploads/8b185f64-9705-41a8-a36e-2756c5af4fe0.png",
        "foundingDate": "2024",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "forkast.in.app@gmail.com",
          "contactType": "customer service"
        },
        "sameAs": []
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="forkast - AI-Powered Family Meal Planning"
        description="forkast's AI-powered meal planning helps busy families organize weekly dinners, get smart dinner suggestions, share the mental load, and build better eating habits. Experience intelligent meal planning with personalized recommendations."
        jsonLd={jsonLd}
      />
      <EarlyAccessBanner />
      <HomepageGuestHeader />
      
      {/* Hero Section */}
      <HeroSectionWeb 
        handleAuthSuccess={handleAuthSuccess}
        handleAuthError={handleAuthError}
      />

      {/* Mobile Screenshots Section - Shown only on mobile/tablet */}
      <section className="py-4 bg-background lg:hidden">
        <div className="container px-4 mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold font-playfair text-charcoal mb-4">
              <span className="text-sunset">Simplify Meal Planning</span> 
            </h2>
          </div>
          <ProductScreenshots />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-16 bg-cream/20">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-playfair text-charcoal">
              <span className="text-sunset">Best Meal Planning App for Families</span>
            </h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* User Testimonial */}
              <div className="text-center relative">
                <div className="text-7xl md:text-8xl text-sunset/60 font-playfair absolute -top-1 left-1/2 transform -translate-x-1/2 -translate-y-1/2 drop-shadow-sm">"</div>
                <blockquote className="text-lg md:text-xl text-charcoal font-inter leading-relaxed mb-6 relative z-10 pt-6">
                  forkast solves our problem of too often winging it when it comes to lunches and dinners. I really like what was built, it has helped a lot <span className="bg-sunset/20 px-2 py-1 rounded-md font-medium text-sunset">taking the stress out of cooking</span>, making us better organised, wasting less and getting inspired to try out new dishes.
                </blockquote>
                <cite className="text-muted-foreground font-inter font-medium">
                  — Susanne
                </cite>
              </div>

              {/* Founder Testimonial */}
              <div className="text-center relative">
                <div className="text-7xl md:text-8xl text-sunset/60 font-playfair absolute -top-1 left-1/2 transform -translate-x-1/2 -translate-y-1/2 drop-shadow-sm">"</div>
                <blockquote className="text-lg md:text-xl text-charcoal font-inter leading-relaxed mb-6 relative z-10 pt-6">
                  I created forkast because my wife and I just hated the daily "what's for dinner" burden after a long day at work. We just wanted <span className="bg-sunset/20 px-2 py-1 rounded-md font-medium text-sunset">something super simple to be better organized</span> with our weekly dinners plans and to get the kids a bit involved.
                </blockquote>
                <cite className="text-muted-foreground font-inter font-medium">
                  — Sebastien, founder
                </cite>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration CTA */}
      <section className="py-8 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth/register">
              <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                Simplify meal planning NOW →
              </Button>
            </Link>
            <Link to="/meal-planning-tips">
              <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                Meal planning tips
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Three Value Points Section */}
      <section className="py-8 md:py-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-playfair text-charcoal">
                <span className="text-sunset">Ultimate Meal Planning Hub for Busy Families</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-left space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-sunset/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Brain className="h-8 w-8 text-sunset" />
                  </div>
                  <h3 className="text-xl font-bold font-playfair text-charcoal">Less mental load</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-sunset mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Use your own recipes your way, forkast keeps them organized
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-sunset mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      One place for everything, so you don't have to keep it all in your head
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-sunset mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      See the full week at a glance, no more dinnertime stress
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-left space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-terracotta/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="h-8 w-8 text-terracotta" />
                  </div>
                  <h3 className="text-xl font-bold font-playfair text-charcoal">Everyone gets involved</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-terracotta mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Invite your partner and kids, so meals don't feel like a solo mission
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-terracotta mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Kids can leave their rating and comments on recipes
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-terracotta mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      See what's going on in your forkast family circle with smart alerts in your activity feed
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-left space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-golden/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-8 w-8 text-golden-dark" />
                  </div>
                  <h3 className="text-xl font-bold font-playfair text-charcoal">Smarter and better</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-golden-dark mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Get AI-powered dinner suggestions
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-golden-dark mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Get a smart shopping list that updates instantly
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-golden-dark mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground font-inter leading-relaxed">
                      Access weekly smart insights to keep your diet in check
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                  Start planning your week NOW →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Useful Features Section */}
      <section className="py-8 md:py-12 bg-cream/20">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-playfair text-charcoal">
                <span className="text-sunset">Useful features to make Meal Planning easier</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Family Circle & Activity Feed</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Connect with your family members and stay updated on meal planning activities with smart notifications and shared updates.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Smart Overview Dashboard</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Get a comprehensive recap of everything you need to know - upcoming meals, shopping lists, and family preferences at a glance.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Quick Notes & Memories</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Add personal notes to recipes and meals to remember what worked, family reactions, and cooking tips for next time.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Flexible Portion Control</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Easily adjust how many people you're cooking and shopping for, with automatic ingredient scaling for different family sizes.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Meal History & Insights</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Look back at what you ate in the last month to track variety, identify favorites, and avoid meal repetition.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">AI Recipe Discovery</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Discover new recipes or rediscover forgotten favorites with AI-powered suggestions based on your family's preferences.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Family Recipe Ratings</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Everyone can rate recipes so you'll know which meals are winners and which ones to skip next time.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">
                  <Link to="/install" className="text-terracotta hover:text-terracotta-dark underline">Install on Any Device</Link>
                </h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Add forkast to your home screen for quick access. Works offline, no app store needed, and lightning-fast on mobile and desktop.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Themed Meal Compositions</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Create special meal combinations for themed nights - Italian night, comfort food Sunday, or healthy Monday meals.
                </p>
              </div>
              
              <div className="bg-background p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold font-playfair text-charcoal mb-3">Personal Recipe Photos</h3>
                <p className="text-muted-foreground font-inter text-sm leading-relaxed">
                  Add your own photos to recipes to remember how dishes should look and share your cooking successes with the family.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beta Invitation Section */}
      <section className="py-8 md:py-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-6">
              We're just getting started and your voice matters.
            </h2>
            
            <p className="text-xl text-muted-foreground font-inter mb-8 leading-relaxed">
              forkast is in beta. That means it's simple, useful — and built to grow with your feedback.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium shadow-lg hover:shadow-xl transition-all duration-200">
                  Signup for free
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/meal-inspiration">
                <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                  See what families are cooking
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuestLanding;
